import React, {useCallback, useRef} from "react";
import {useSiren} from "../SirenContext";
import {useGetSirenMessageContents} from "../hooks/useGetSirenMessageContents";
import {SirenMessageContent} from "../typedefs";
import {useNavigateWithClientID} from "../hooks/useNavigateWithClientID";
import {useAuth} from "../../../context/user";
import {Action, ActionSelect} from "../../../components/ui/ActionSelect";
import {usePlatformDeleteModal} from "../hooks/useDeleteModal";
import {UserName} from "../../../components/data/UserName";
import {PaginatedSelector} from "../../../components/paginated-selector/paginated-selector";
import {useCopyID} from "../../../lib/misc/useCopyID";

export function SirenMessageContentList() {
    const {clientID} = useSiren()
    const {isSuperAdmin} = useAuth()
    const {navigateWithClientID} = useNavigateWithClientID()
    const getItems = useGetSirenMessageContents(clientID)
    const refetchRef = useRef<(() => void) | null>(null)

    const getColumns = useCallback((item: SirenMessageContent) => [
        item.name,
        <UserName id={item.createdBy}/>,
        <UserName id={item.updatedBy}/>,
    ], [])

    const renderActions = useCallback((item: SirenMessageContent) => (
        <SirenMessageContentActions item={item} refetch={() => refetchRef.current?.()}/>
    ), [])

    if (!clientID && isSuperAdmin) return null;

    return (
        <PaginatedSelector
            key={clientID}
            headers={["Name", "Created By", "Last Updated By"]}
            getItems={getItems}
            getColumns={getColumns}
            disableSelect={true}
            renderActions={renderActions}
            onColumnClick={(item) => navigateWithClientID(`siren/message-contents/${item.id}/edit`)}
            syncWithUrl={true}
            onRefetchReady={(refetch) => { refetchRef.current = refetch }}
        />
    )
}

function SirenMessageContentActions({item, refetch}: { item: SirenMessageContent; refetch: () => void }) {
    const {navigateWithClientID} = useNavigateWithClientID()
    const {copyID, isCopied} = useCopyID()

    const {DeleteModal, openDeleteModal} = usePlatformDeleteModal(
        "Message Content",
        item.name,
        `api/siren/message-contents/${item.id}`,
        refetch,
    )

    return (
        <>
            <DeleteModal/>
            <ActionSelect label="Actions">
                <Action label="Edit"
                        onSelect={() => navigateWithClientID(`siren/message-contents/${item.id}/edit`)}/>
                <Action label={isCopied(item.id) ? "Copied!" : "Copy ID"} onSelect={() => copyID(item.id)}/>
                <Action label="Delete" onSelect={openDeleteModal}/>
            </ActionSelect>
        </>
    )
}
